import { useMutation } from '@apollo/client';
import { RePostInput } from '@/__generated__/graphql.ts';
import { gql } from '@/__generated__/index.ts';
import { updateFeedCache } from '@/apollo-cache-utils/update-feed-cache.ts';
import { useAuthInfo } from '@/routes/_app/use-auth-info.ts';
import { RepostCardFragment } from './repost-card-fragment.ts';

const RePost = gql(`
  mutation Repost($input: RePostInput!) {
    repost(input: $input) {
      ...RepostCardFragment
    }
  }
`);

export const useRePost = (input: RePostInput) => {
  const [rePostMutation, result] = useMutation(RePost);
  const { id: meId } = useAuthInfo();

  const rePost = () => {
    return rePostMutation({
      variables: { input },
      update(cache, { data }) {
        const addPost = data?.repost;
        if (addPost) {
          cache.modify({
            id: `User:${meId}`,
            fields: {
              posts(postsPage) {
                const newPostRef = cache.writeFragment({
                  fragment: RepostCardFragment,
                  fragmentName: 'RepostCardFragment',
                  data: addPost,
                });

                return updateFeedCache(postsPage, newPostRef);
              },
            },
          });
        }
      },
    });
  };

  return [rePost, result] as const;
};

const UnRePost = gql(`
  mutation UnRepost($repostId: ID!) {
    unRepost(repostId: $repostId)
  }
`);

export const useUnRePost = (repostId: string) => {
  const { id: meId } = useAuthInfo();

  const [unRePostMutation, result] = useMutation(UnRePost, {
    update(cache, { data }) {
      const repostRef = cache.identify({
        id: repostId,
        __typename: 'RePost',
      });
      const unRepost = data?.unRepost;
      if (unRepost) {
        cache.modify({
          id: `User:${meId}`,
          fields: {
            posts(postsPage) {
              const idx = postsPage.items.findIndex(
                (item: { __ref: string }) => item.__ref === repostRef,
              );
              return postsPage.items
                .slice(0, idx)
                .concat(postsPage.items.slice(idx + 1));
            },
          },
        });
      }
    },
  });

  const unRePost = () => {
    return unRePostMutation({
      variables: { repostId },
      optimisticResponse: {
        unRepost: true,
      },
    });
  };

  return [unRePost, result] as const;
};
